import React, { useState } from "react";
import { connect } from "react-redux";
import { func, array, bool, object, number, oneOfType } from "prop-types";
import { Button, Form, Modal } from "react-bootstrap";
import { shouldOptionShow } from "./helperFunctions";

const DownloadTestResults = ({
  changeAction,
  onDownloadResults,
  candidateIds,
  handleModalCancel,
  fromSearch,
  userData,
  featureFlags,
  companyInfo,
  showWorkStyles,
  candidateStatus,
  isEPPCompleted,
  isBulkAction = false,
  areProctoringRecordingsAvailable = false,
}) => {
  const [downloadDocuments, setDownloadDocuments] = useState({
    selectAll: false,
    summary: false,
    report: false,
    workplaceInsights: false,
    workStyles: false,
    resume: false,
    application: false,
    documents: false,
    managerGuide: false,
    proctoringRecordings: false,
  });

  const {
    selectAll,
    summary,
    report,
    workplaceInsights,
    workStyles,
    resume,
    application,
    documents,
    managerGuide,
    proctoringRecordings,
  } = downloadDocuments;

  const showManagerGuideOption = shouldOptionShow(
    "managerGuide",
    userData,
    companyInfo,
    featureFlags,
    candidateStatus,
    isBulkAction
  );

  const showDownloadProctoringRecordingsOption = shouldOptionShow(
    "proctoringRecordings",
    userData,
    companyInfo,
    featureFlags,
    candidateStatus,
    isBulkAction
  );

  const isInsightUser = userData && userData.partnerAccountId === 47;
  const permissionScoreReports = userData && userData.permissionScoreReports;

  /* --------- Event Handlers ---------- */

  const handleChangeAction = (isActionDone) => {
    if (fromSearch) {
      handleModalCancel();
    } else {
      changeAction(false, isActionDone);
    }
  };

  const handleDownloadZipConfirm = () => {
    const downloadObj = {
      testTakerIds: candidateIds,
      options: downloadDocuments,
    };
    onDownloadResults(downloadObj);
    handleChangeAction(true);
  };

  const handleCheckInput = (e) => {
    const inputVal = e.target.dataset.value;
    setDownloadDocuments({
      ...downloadDocuments,
      [inputVal]: !downloadDocuments[inputVal],
    });
  };

  // Note: SelectAll checkbox is not available in Search Container
  const handleSelectAll = () => {
    const inputVal = selectAll;
    setDownloadDocuments({
      selectAll: !inputVal,
      summary: !inputVal,
      report: permissionScoreReports ? !inputVal : false,
      workplaceInsights: !isInsightUser ? !inputVal : false,
      workStyles: showWorkStyles ? !inputVal : false,
      resume: !inputVal,
      application: !isInsightUser ? !inputVal : false,
      documents: !inputVal,
      managerGuide: showManagerGuideOption ? !inputVal : false,
      proctoringRecordings: showDownloadProctoringRecordingsOption
        ? !inputVal
        : false,
    });
  };

  return (
    <div>
      <Modal.Header closeButton={fromSearch}>
        <h4>Download Test Results</h4>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>Which documents would you like to download?</strong>
        </p>
        {!fromSearch ? (
          <Form.Check
            onChange={handleSelectAll}
            checked={selectAll}
            data-value="selectAll"
            label="Select All"
            id="selectAll"
          />
        ) : null}
        {permissionScoreReports || isInsightUser ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={summary}
            data-value="summary"
            label="Candidate Summary"
            id="candidateSummary"
          />
        ) : null}
        {permissionScoreReports ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={report}
            data-value="report"
            label="Score Reports"
            id="scoreReports"
          />
        ) : null}
        {!isInsightUser && !fromSearch && permissionScoreReports ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={workplaceInsights}
            data-value="workplaceInsights"
            label="Workplace Insights"
            id="workplaceInsights"
          />
        ) : null}
        {showManagerGuideOption && isEPPCompleted && permissionScoreReports && (
          <Form.Check
            type="checkbox"
            onChange={handleCheckInput}
            checked={managerGuide}
            data-value="managerGuide"
            label="Manager Guide"
            id="managerGuide"
            // className="form-check-input-style"
          />
        )}
        {showWorkStyles && !fromSearch && permissionScoreReports ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={workStyles}
            data-value="workStyles"
            label="Work Styles"
            id="workStyles"
          />
        ) : null}
        <Form.Check
          onChange={handleCheckInput}
          checked={resume}
          data-value="resume"
          label="Resume"
          id="resume"
        />
        {!isInsightUser ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={application}
            data-value="application"
            label="Job Applications"
            id="jobApplications"
          />
        ) : null}
        <Form.Check
          onChange={handleCheckInput}
          checked={documents}
          data-value="documents"
          label="Additional Documents"
          id="additionalDocuments"
        />
        {showDownloadProctoringRecordingsOption &&
        areProctoringRecordingsAvailable ? (
          <Form.Check
            onChange={handleCheckInput}
            checked={proctoringRecordings}
            data-value="proctoringRecordings"
            label="Proctoring Recordings"
            id="proctoringRecordings"
          />
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => handleChangeAction(false)}
          id="cancelDownloadResults"
          variant="outline-primary"
          className="btn-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownloadZipConfirm}
          disabled={
            !summary &&
            !report &&
            !workplaceInsights &&
            !workStyles &&
            !resume &&
            !application &&
            !documents &&
            !managerGuide &&
            !proctoringRecordings
          }
          id="downloadResultsZip"
        >
          Download
        </Button>
      </Modal.Footer>
    </div>
  );
};

DownloadTestResults.propTypes = {
  changeAction: func,
  onDownloadResults: func,
  candidateIds: array,
  handleModalCancel: func,
  fromSearch: bool,
  userData: object,
  featureFlags: object,
  companyInfo: object,
  showWorkStyles: bool,
  candidateStatus: number,
  isEPPCompleted: oneOfType([bool, object]),
  isBulkAction: bool,
  areProctoringRecordingsAvailable: bool,
};

const mapStateToProps = (state) => ({
  userData: state.get("App").get("userData"),
  featureFlags: state.get("App").get("featureFlags"),
  companyInfo: state.get("App").get("companyInfo"),
});

export default connect(mapStateToProps)(DownloadTestResults);
